import { useEffect, useRef, useState } from "react";
import { Bell, Volume2, VolumeX, ShoppingBag, MapPin, Phone, X, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import api, { formatUSD } from "@/lib/api";
import { useNavigate } from "react-router-dom";

const POLL_INTERVAL = 6000; // 6s
const SEEN_KEY = "dsf_admin_seen_orders";
const ACK_KEY = "dsf_admin_acked_orders";
const SIREN_INTERVAL_MS = 1200; // loop period — keeps ringing until accept/reject
const ACTIONABLE_STATUSES = ["paid", "confirmed", "preparing", "ready", "delivering", "awaiting_acceptance"];

const REJECT_REASONS = [
  "Out of ingredients / product unavailable",
  "Kitchen is too busy right now",
  "Closed / outside opening hours",
  "Unable to reach customer",
  "Pickup time not feasible",
  "Suspected fraud",
  "Other (specify below)",
];

export default function NewOrderNotifier() {
  const navigate = useNavigate();
  const [muted, setMuted] = useState(() => localStorage.getItem("dsf_admin_muted") === "1");
  const [audioReady, setAudioReady] = useState(false);
  const [queue, setQueue] = useState([]); // unacknowledged orders FIFO
  const [showRejectDialog, setShowRejectDialog] = useState(false);
  const initializedRef = useRef(false);
  const seenRef = useRef(new Set(JSON.parse(localStorage.getItem(SEEN_KEY) || "[]")));
  const ackRef = useRef(new Set(JSON.parse(localStorage.getItem(ACK_KEY) || "[]")));
  const audioCtxRef = useRef(null);
  const sirenTimerRef = useRef(null);
  const watchdogTimerRef = useRef(null);
  const ringCountRef = useRef(0);
  const [ringCount, setRingCount] = useState(0);

  // -------- Audio --------
  const ensureAudio = async () => {
    try {
      if (!audioCtxRef.current || audioCtxRef.current.state === "closed") {
        audioCtxRef.current = new (window.AudioContext || window.webkitAudioContext)();
      }
      if (audioCtxRef.current.state === "suspended") {
        await audioCtxRef.current.resume();
      }
      setAudioReady(true);
      return audioCtxRef.current;
    } catch {
      return null;
    }
  };

  const playSiren = (ctx) => {
    if (!ctx || ctx.state !== "running") return;
    // Aggressive Uber-style alert: rapid high-low-high-low-high tones
    [880, 1320, 880, 1320, 880].forEach((freq, i) => {
      setTimeout(() => {
        try {
          if (!audioCtxRef.current || audioCtxRef.current.state !== "running") return;
          const o = ctx.createOscillator();
          const g = ctx.createGain();
          o.connect(g);
          g.connect(ctx.destination);
          o.type = "square";
          o.frequency.value = freq;
          const t = ctx.currentTime;
          g.gain.setValueAtTime(0.001, t);
          g.gain.exponentialRampToValueAtTime(0.5, t + 0.02); // louder
          g.gain.exponentialRampToValueAtTime(0.001, t + 0.22);
          o.start(t);
          o.stop(t + 0.25);
        } catch {
          /* osc creation failed */
        }
      }, i * 180);
    });
    // Also vibrate phones
    try {
      if (navigator.vibrate) navigator.vibrate([300, 100, 300, 100, 600]);
    } catch {
      /* vibration unsupported */
    }
    ringCountRef.current += 1;
    setRingCount(ringCountRef.current);
  };

  const startSirenLoop = async () => {
    if (muted) return;
    const ctx = await ensureAudio();
    if (!ctx) return;
    if (sirenTimerRef.current) return; // already running
    ringCountRef.current = 0;
    // Defer state update to next tick to avoid set-state-in-effect lint
    setTimeout(() => setRingCount(0), 0);
    playSiren(ctx);
    sirenTimerRef.current = setInterval(() => playSiren(ctx), SIREN_INTERVAL_MS);

    // Watchdog: every 2s, make sure the AudioContext is still alive.
    // Browsers (especially mobile) may suspend audio when the tab is backgrounded;
    // this guarantees the siren resumes the moment the tab becomes audible again.
    if (watchdogTimerRef.current) clearInterval(watchdogTimerRef.current);
    watchdogTimerRef.current = setInterval(async () => {
      try {
        if (audioCtxRef.current && audioCtxRef.current.state === "suspended") {
          await audioCtxRef.current.resume();
        }
      } catch {
        /* ignore */
      }
    }, 2000);
  };

  const stopSirenLoop = () => {
    if (sirenTimerRef.current) {
      clearInterval(sirenTimerRef.current);
      sirenTimerRef.current = null;
    }
    if (watchdogTimerRef.current) {
      clearInterval(watchdogTimerRef.current);
      watchdogTimerRef.current = null;
    }
  };

  const testSound = async () => {
    const ctx = await ensureAudio();
    if (!ctx) {
      toast.error("Audio blocked by browser. Allow sound for this site.");
      return;
    }
    playSiren(ctx);
    toast.success("Sound activated", { description: "You'll hear this every new order." });
  };

  // -------- Polling --------
  useEffect(() => {
    if ("Notification" in window && Notification.permission === "default") {
      Notification.requestPermission().catch(() => {});
    }
  }, []);

  const poll = async () => {
    try {
      const r = await api.get("/admin/orders", { params: { limit: 50 } });
      const orders = Array.isArray(r.data) ? r.data : (r.data?.items || r.data?.orders || []);
      const actionable = orders.filter((o) => ACTIONABLE_STATUSES.includes(o.status));

      if (!initializedRef.current) {
        actionable.forEach((o) => seenRef.current.add(o.id));
        localStorage.setItem(SEEN_KEY, JSON.stringify(Array.from(seenRef.current).slice(-300)));
        initializedRef.current = true;
        return;
      }
      const newOnes = actionable.filter((o) => !seenRef.current.has(o.id) && !ackRef.current.has(o.id));
      if (newOnes.length > 0) {
        newOnes.forEach((o) => seenRef.current.add(o.id));
        localStorage.setItem(SEEN_KEY, JSON.stringify(Array.from(seenRef.current).slice(-300)));
        setQueue((q) => [...q, ...newOnes]);
        // Fire OS-level notification too
        if ("Notification" in window && Notification.permission === "granted") {
          newOnes.forEach((o) => {
            const isFT = o.delivery_method === "pickup_foodtruck";
            const n = new Notification(`${isFT ? "🚚 FOOD TRUCK" : "🏪 RESTAURANT"} · #${o.order_number}`, {
              body: `${o.customer_name} · $${(o.total || 0).toFixed(2)}`,
              icon: "/favicon.png", tag: o.id, requireInteraction: true,
            });
            n.onclick = () => { window.focus(); navigate("/admin/commandes"); };
          });
        }
      }
    } catch {
      /* network blip */
    }
  };

  useEffect(() => {
    poll();
    const iv = setInterval(poll, POLL_INTERVAL);
    const onVisibility = () => { if (document.visibilityState === "visible") poll(); };
    document.addEventListener("visibilitychange", onVisibility);
    return () => { clearInterval(iv); document.removeEventListener("visibilitychange", onVisibility); };
    // eslint-disable-next-line
  }, []);

  // Start/stop siren loop whenever the queue toggles between empty and non-empty
  useEffect(() => {
    let cancelled = false;
    const t = setTimeout(() => {
      if (cancelled) return;
      if (queue.length > 0 && !muted) startSirenLoop();
      else stopSirenLoop();
    }, 0);
    return () => { cancelled = true; clearTimeout(t); stopSirenLoop(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps, react-hooks/exhaustive-deps
  }, [queue.length, muted]);

  // Stop everything on unmount
  useEffect(() => () => stopSirenLoop(), []);

  const toggleMute = async () => {
    const v = !muted;
    setMuted(v);
    localStorage.setItem("dsf_admin_muted", v ? "1" : "0");
    if (!v) await ensureAudio();
  };

  const acceptCurrent = async () => {
    const head = queue[0];
    if (!head) return;
    if (head.payment_status === "authorized" || head.status === "awaiting_acceptance") {
      try {
        await api.post(`/admin/orders/${head.id}/accept`);
        toast.success(`Order ${head.order_number} accepted — card captured`);
      } catch (e) {
        toast.error(e.response?.data?.detail || "Accept failed");
        return;
      }
    }
    ackRef.current.add(head.id);
    localStorage.setItem(ACK_KEY, JSON.stringify(Array.from(ackRef.current).slice(-500)));
    setQueue((q) => q.slice(1));
    navigate("/admin/commandes");
  };

  // Open the reason dialog (rejection requires a written reason)
  const openRejectDialog = () => setShowRejectDialog(true);

  const submitReject = async (reason) => {
    const head = queue[0];
    if (!head) return;
    try {
      if (head.payment_status === "authorized" || head.status === "awaiting_acceptance") {
        await api.post(`/admin/orders/${head.id}/reject`, { reason });
        toast.success(`Order ${head.order_number} rejected — authorization cancelled`);
      } else {
        await api.post(`/admin/orders/${head.id}/reject`, { reason });
        toast.success(`Order ${head.order_number} rejected`);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Reject failed");
      return;
    }
    ackRef.current.add(head.id);
    localStorage.setItem(ACK_KEY, JSON.stringify(Array.from(ackRef.current).slice(-500)));
    setQueue((q) => q.slice(1));
    setShowRejectDialog(false);
  };

  const head = queue[0];

  // -------- Render --------
  return (
    <>
      <div className="flex items-center gap-2" data-testid="new-order-notifier">
        <button
          onClick={() => navigate("/admin/commandes")}
          className="relative w-9 h-9 rounded-full bg-white/10 hover:bg-white/15 flex items-center justify-center transition"
          title="View orders"
          data-testid="notifier-bell"
        >
          <Bell size={15} className="text-white" />
          {queue.length > 0 && (
            <span className="absolute -top-1 -right-1 bg-[var(--dd-coral)] text-white text-[9px] font-bold w-4 h-4 rounded-full flex items-center justify-center ring-2 ring-[var(--dd-green)] animate-pulse">
              {queue.length > 9 ? "9+" : queue.length}
            </span>
          )}
        </button>
        <button
          onClick={toggleMute}
          className="w-9 h-9 rounded-full bg-white/10 hover:bg-white/15 flex items-center justify-center transition"
          title={muted ? "Enable sounds" : "Mute sounds"}
          data-testid="notifier-mute"
        >
          {muted ? <VolumeX size={14} className="text-white/70" /> : <Volume2 size={14} className="text-[var(--dd-mustard)]" />}
        </button>
        {!muted && !audioReady && (
          <button
            onClick={testSound}
            className="hidden md:flex items-center gap-1.5 text-[10px] uppercase tracking-wider font-bold bg-[var(--dd-mustard)] text-[var(--dd-green)] rounded-full px-3 h-9 hover:opacity-90 transition animate-pulse"
            title="Click to enable sound (browser requires user gesture)"
            data-testid="notifier-test-sound"
          >
            🔔 Activate sound
          </button>
        )}
      </div>

      {/* FULLSCREEN BLOCKING MODAL (Uber-style) */}
      {head && !showRejectDialog && (
        <NewOrderModal
          order={head}
          remaining={queue.length}
          ringCount={ringCount}
          onAccept={acceptCurrent}
          onReject={openRejectDialog}
        />
      )}

      {/* REJECT REASON DIALOG */}
      {head && showRejectDialog && (
        <RejectReasonDialog
          order={head}
          onCancel={() => setShowRejectDialog(false)}
          onSubmit={submitReject}
        />
      )}
    </>
  );
}

/* ---------------- Modal ---------------- */
function NewOrderModal({ order, remaining, ringCount, onAccept, onReject }) {
  const isFT = order.delivery_method === "pickup_foodtruck";
  const locLabel = isFT ? "FOOD TRUCK" : "RESTAURANT";
  const locEmoji = isFT ? "🚚" : "🏪";
  const accent = isFT ? "bg-[var(--dd-coral)]" : "bg-[var(--dd-green)]";
  const accentRing = isFT ? "ring-[var(--dd-coral)]" : "ring-[var(--dd-green)]";
  const itemsCount = (order.items || []).reduce((s, it) => s + it.quantity, 0);
  const awaitingAccept = order.payment_status === "authorized" || order.status === "awaiting_acceptance";

  // Lock body scroll while modal open
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, []);

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4"
      data-testid="new-order-modal"
      style={{ animation: "dsfFlashBg 1.4s ease-in-out infinite" }}
    >
      <style>{`
        @keyframes dsfFlashBg {
          0%, 100% { background-color: rgba(0,0,0,0.65); }
          50% { background-color: rgba(232, 93, 69, 0.85); }
        }
        @keyframes dsfFlashBorder {
          0%, 100% { box-shadow: 0 0 0 0 rgba(232, 93, 69, 0.7), 0 30px 80px rgba(0,0,0,0.4); }
          50% { box-shadow: 0 0 0 24px rgba(232, 93, 69, 0), 0 30px 80px rgba(0,0,0,0.5); }
        }
        @keyframes dsfRingPulse {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.06); }
        }
      `}</style>
      <div
        className={`relative bg-white rounded-3xl max-w-xl w-full overflow-hidden shadow-2xl ring-4 ${accentRing}`}
        style={{ animation: "dsfFlashBorder 1.4s ease-in-out infinite" }}
      >
        {/* RINGING indicator pill */}
        {ringCount > 0 && (
          <div
            className="absolute top-3 right-3 z-10 bg-white/95 text-[var(--dd-coral)] text-[10px] font-bold uppercase tracking-[0.18em] px-2.5 py-1 rounded-full flex items-center gap-1.5 shadow-md"
            style={{ animation: "dsfRingPulse 0.6s ease-in-out infinite" }}
            data-testid="ring-counter"
          >
            <span className="w-1.5 h-1.5 bg-[var(--dd-coral)] rounded-full animate-ping" />
            Ringing… ({ringCount})
          </div>
        )}

        {/* Header band */}
        <div className={`${accent} text-white px-8 py-6 text-center`}>
          <div className="text-xs uppercase tracking-[0.32em] opacity-90 font-bold">
            {remaining > 1 ? `${remaining} new orders` : "New order"}
          </div>
          <div className="text-6xl mt-2 mb-1">{locEmoji}</div>
          <div className="font-bold text-3xl tracking-wide">{locLabel}</div>
          <div className="text-sm opacity-90 mt-1">Prepare immediately</div>
        </div>

        {/* Body */}
        <div className="p-8 space-y-5">
          <div className="text-center">
            <div className="text-[10px] uppercase tracking-[0.28em] text-[var(--dsf-text-muted)] font-bold">Order</div>
            <div className="font-mono text-2xl font-bold text-[var(--dd-green)] mt-1">{order.order_number}</div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <Stat icon={ShoppingBag} label="Items" value={`${itemsCount}`} />
            <Stat icon={MapPin} label="Total" value={formatUSD(order.total || 0)} />
          </div>

          <div className="rounded-2xl bg-[var(--dd-bone)] p-4">
            <div className="text-[10px] uppercase tracking-wider text-[var(--dsf-text-muted)] font-bold mb-2">Customer</div>
            <div className="font-semibold text-[var(--dd-green)] text-base">{order.customer_name}</div>
            <div className="text-sm text-[var(--dsf-text-secondary)] flex items-center gap-1.5 mt-1">
              <Phone size={12} /> {order.customer_phone}
            </div>
          </div>

          {(order.items || []).length > 0 && (
            <div>
              <div className="text-[10px] uppercase tracking-wider text-[var(--dsf-text-muted)] font-bold mb-2">To prepare</div>
              <div className="space-y-1.5">
                {(order.items || []).slice(0, 5).map((it, i) => (
                  <div key={i} className="flex justify-between text-sm">
                    <span className="text-[var(--dd-green)] font-medium">{it.name}</span>
                    <span className="text-[var(--dsf-text-muted)] font-mono">× {it.quantity}</span>
                  </div>
                ))}
                {(order.items || []).length > 5 && (
                  <div className="text-xs text-[var(--dsf-text-muted)] italic">+ {(order.items || []).length - 5} more items…</div>
                )}
              </div>
            </div>
          )}

          {order.notes && (
            <div className="rounded-xl bg-amber-50 border-l-4 border-amber-400 p-3">
              <div className="text-[10px] uppercase tracking-wider text-amber-800 font-bold">Customer note</div>
              <div className="text-sm text-amber-900 mt-1">{order.notes}</div>
            </div>
          )}

          <button
            onClick={onAccept}
            className={`w-full ${accent} text-white font-bold py-5 rounded-2xl text-lg uppercase tracking-[0.18em] hover:opacity-90 active:scale-[0.98] transition shadow-lg`}
            data-testid="accept-order-button"
            autoFocus
          >
            {awaitingAccept ? "Accept & charge card" : "Accept — view order"}
          </button>
          <button
            onClick={onReject}
            className="w-full bg-white text-[var(--dd-coral)] border-2 border-[var(--dd-coral)] font-bold py-3 rounded-2xl text-sm uppercase tracking-[0.18em] hover:bg-red-50 active:scale-[0.98] transition"
            data-testid="reject-order-button"
          >
            {awaitingAccept ? "Reject & refund" : "Reject order"}
          </button>
          <div className="text-center text-xs text-[var(--dsf-text-muted)]">
            {awaitingAccept
              ? "Card is authorized but NOT charged yet. Accepting will capture the payment."
              : "Siren and flashing stop only when you accept or reject this order."}
          </div>
        </div>
      </div>
    </div>
  );
}

/* ---------------- Reject reason dialog ---------------- */
function RejectReasonDialog({ order, onCancel, onSubmit }) {
  const [selected, setSelected] = useState(REJECT_REASONS[0]);
  const [customText, setCustomText] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const isCustom = selected === REJECT_REASONS[REJECT_REASONS.length - 1];
  const reason = isCustom ? customText.trim() : selected;
  const canSubmit = !!reason && reason.length >= 4;

  return (
    <div
      className="fixed inset-0 z-[110] bg-black/80 flex items-center justify-center p-4"
      data-testid="reject-reason-dialog"
    >
      <div className="bg-white rounded-3xl max-w-lg w-full overflow-hidden shadow-2xl">
        <div className="bg-[var(--dd-coral)] text-white px-6 py-5 flex items-center gap-3">
          <AlertTriangle size={22} />
          <div>
            <div className="text-[10px] uppercase tracking-[0.28em] opacity-90 font-bold">Reject order</div>
            <div className="font-bold text-lg">{order.order_number}</div>
          </div>
        </div>
        <div className="p-6 space-y-5">
          <p className="text-sm text-[var(--dsf-text-secondary)]">
            <strong>Refusing requires a reason</strong> — the customer will be notified and the card authorization
            will be released immediately. Be honest, it builds trust.
          </p>
          <div className="space-y-2" data-testid="reject-reason-options">
            {REJECT_REASONS.map((r) => (
              <label
                key={r}
                className={`flex items-center gap-3 p-3 rounded-xl border-2 cursor-pointer transition ${
                  selected === r
                    ? "border-[var(--dd-coral)] bg-[var(--dd-coral)]/5"
                    : "border-black/5 hover:border-[var(--dd-coral)]/40"
                }`}
              >
                <input
                  type="radio"
                  name="reject-reason"
                  className="accent-[var(--dd-coral)]"
                  checked={selected === r}
                  onChange={() => setSelected(r)}
                  data-testid={`reject-reason-radio-${r.split(" ")[0].toLowerCase()}`}
                />
                <span className="text-sm font-medium text-[var(--dd-green)]">{r}</span>
              </label>
            ))}
          </div>
          {isCustom && (
            <textarea
              value={customText}
              onChange={(e) => setCustomText(e.target.value)}
              rows={3}
              maxLength={300}
              placeholder="Explain to the customer (min. 4 characters)…"
              className="w-full border-2 border-[var(--dd-coral)]/30 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-[var(--dd-coral)]"
              data-testid="reject-reason-custom-text"
              autoFocus
            />
          )}
          <div className="flex gap-3">
            <button
              onClick={onCancel}
              className="flex-1 bg-white text-[var(--dd-green)] border-2 border-[var(--dd-green)]/20 font-bold py-3 rounded-xl text-sm uppercase tracking-wider hover:bg-[var(--dd-bone)] transition"
              data-testid="reject-cancel-button"
            >
              Cancel
            </button>
            <button
              onClick={async () => {
                if (!canSubmit || submitting) return;
                setSubmitting(true);
                try { await onSubmit(reason); } finally { setSubmitting(false); }
              }}
              disabled={!canSubmit || submitting}
              className="flex-1 bg-[var(--dd-coral)] text-white font-bold py-3 rounded-xl text-sm uppercase tracking-wider hover:opacity-90 active:scale-[0.98] transition disabled:opacity-40 disabled:cursor-not-allowed"
              data-testid="reject-confirm-button"
            >
              {submitting ? "Rejecting…" : "Confirm reject"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function Stat({ icon: Icon, label, value }) {
  return (
    <div className="rounded-2xl border-2 border-[var(--dd-bone)] p-4 text-center">
      <Icon className="mx-auto text-[var(--dd-green)] mb-1" size={18} />
      <div className="text-[10px] uppercase tracking-wider text-[var(--dsf-text-muted)] font-bold">{label}</div>
      <div className="font-bold text-xl text-[var(--dd-green)] mt-1">{value}</div>
    </div>
  );
}
